import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import PageInterface from '@app/Interface/PageInterface';
import { Grid, GridItem, PageSection, Title } from '@patternfly/react-core';
import User from '@app/Interface/UserInterface';
import NotificationSettings from '@app/Components/Notifications/NotificationSettings';
import UpdateNotificationField from '@app/Components/Notifications/UpdateNotificationField';
import ChartSettings from '@app/Components/Notifications/ChartSettings';
import UserChartNotificationSettingsModel from '@app/Model/UserChartNotificationSettingsModel';
import UserChartNotificationSettingsInterface from '@app/Interface/UserChartNotificationSettingsInterface';
import ChartSettingEdit from '@app/Components/Notifications/ChartSettingEdit';
import htmlParser from '@app/utils/htmlParser';
import PageHelper from '@app/Service/PageHelper';

interface NotificationsPageProps {
    pageData: PageModel;
    currentUser: User;
}

const NotificationsPage: React.FC<NotificationsPageProps> = ({ pageData, currentUser, children }) => {

    const [editSetting, setEditSetting] = React.useState(false);

    const [editChartSetting, setEditChartSetting] = React.useState(false);

    const [editChartSettingPage, setEditChartSettingPage] = React.useState<PageInterface | null>(null);

    const [chartSetting, setChartSetting] = React.useState<UserChartNotificationSettingsInterface>(new UserChartNotificationSettingsModel());

    const [editSettingType, setEditSettingType] = React.useState<"Email" | "Text">("Email");

    const [allowNotifications, setAllowNotifications] = React.useState(currentUser.enableNotifications);

    const [chartPagesAreLoading, setChartPagesAreLoading] = React.useState(true);

    const [chartPages, setChartPages] = React.useState<PageInterface[]>([]);

    const [userChartSettings, setUserChartSettings] = React.useState<UserChartNotificationSettingsInterface[]>([]);

    // useEffect() dependency can't detect array changes (only if new item is added/removed). 
    // use this variable to detect changes
    const fpuChartNotificationSettingsString = JSON.stringify(currentUser.fpuChartNotificationSettings);

    /**
     * Sets the value for edit setting and which setting to edit
     * @param value 
     */
    const handleEditSettingButton = (value: boolean, type: "Email" | "Text") => {

        setEditSetting(value);

        setEditSettingType(type);

    };

    const handleEditChartSetting = (show: boolean, setting: UserChartNotificationSettingsInterface, page: PageInterface | null) => {

        setEditChartSetting(show);

        setChartSetting(setting);

        setEditChartSettingPage(page);

    }

    const handleBackButton = () => {

        setEditSetting(false);

        setEditChartSetting(false);

        setEditChartSettingPage(null);

    };

    // check to see if enableNotifications changed
    React.useEffect(() => {

        setAllowNotifications(currentUser.enableNotifications);

    }, [currentUser.enableNotifications]);

    // get a list of all the chart pages. 
    // this will provide baseline chart notifications.
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        // get the pages
        const getPages = async () => {

            const pages = await PageHelper.getAllPagesByTemplateName(controller, currentUser.currentFpuId, "chart_template.php");

            if (mounted) {
                setChartPages(pages);

                setChartPagesAreLoading(false);

            }

        }

        // if fpu id changed, make sure we show the main notifications page.
        // example: If editing a chart setting and click a new FPU, go back to the main page.
        setEditSetting(false);

        setEditChartSetting(false);

        setEditChartSettingPage(null);

        setChartPagesAreLoading(true);

        getPages();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [currentUser.currentFpuId]);

    // get the user's current setting for chart notifications. 
    React.useEffect(() => {

        let fpuSettings: UserChartNotificationSettingsInterface[] = [];

        if (typeof currentUser.fpuChartNotificationSettings !== "boolean") {

            fpuSettings = currentUser.fpuChartNotificationSettings.filter(setting => setting.fpu === currentUser.currentFpuId);

        }

        setUserChartSettings(fpuSettings);

    }, [currentUser.currentFpuId, currentUser.fpuChartNotificationSettings, fpuChartNotificationSettingsString]);

    // check for editing a setting
    if (editSetting) {

        return (
            <PageSection>
                <Title headingLevel="h1" className="pf-u-mb-md">Edit {editSettingType} notification</Title>
                <UpdateNotificationField
                    type={editSettingType}
                    currentUser={currentUser}
                    handleEditSettingButton={handleEditSettingButton}
                />
            </PageSection>
        );
    }

    // check if editing a chart setting
    if (editChartSetting) {

        const recommendedSetting = new UserChartNotificationSettingsModel();

        const fromPageSetting = pageData.acf.default_notification_thresholds?.find(threshold => threshold.analyte === chartSetting.chart);

        if (fromPageSetting) {
            recommendedSetting.fpu = currentUser.currentFpuId;
            recommendedSetting.chart = fromPageSetting.analyte;
            recommendedSetting.threshold_range = fromPageSetting.threshold_range;
            recommendedSetting.threshold_value_min = fromPageSetting.threshold_value_min;
            recommendedSetting.threshold_value_max = fromPageSetting.threshold_value_max;
            recommendedSetting.threshold_display_value_min = fromPageSetting.threshold_value_min.toString();
            recommendedSetting.threshold_display_value_max = fromPageSetting.threshold_value_max.toString();
            recommendedSetting.enable_guardrail = fromPageSetting.enable_guardrail;
            recommendedSetting.guardrail_threshold = fromPageSetting.guardrail_threshold;
            recommendedSetting.guardrail_value = fromPageSetting.guardrail_value;
            recommendedSetting.isNew = false;
            recommendedSetting.active = false;
        }

        return (
            <ChartSettingEdit
                currentUser={currentUser}
                setting={chartSetting}
                handleBackButton={handleBackButton}
                recommendedSetting={recommendedSetting}
                chartPage={editChartSettingPage}
            />
        );

    }

    return (
        <PageSection>

            <div>
                <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                <div className="pf-c-content">
                    {htmlParser.htmlToJSX(pageData.content)}
                </div>
            </div>

            <Grid
                hasGutter
                sm={12}
                md={6}
                lg={6}
                xl={6}
                xl2={6}
            >
                <GridItem>
                    <NotificationSettings pageData={pageData} currentUser={currentUser} handleEditSettingButton={handleEditSettingButton} />
                    
                </GridItem>
                {allowNotifications &&

                    <GridItem>
                        <Title headingLevel="h2">Chart Notification Settings</Title>
                        <p className="pf-u-mb-md">{pageData.acf.notification_section_description}</p>
                        <ChartSettings
                            currentUser={currentUser}
                            notificationPageData={pageData}
                            handleEditChartSetting={handleEditChartSetting}
                            chartPagesAreLoading={chartPagesAreLoading}
                            chartPages={chartPages}
                            userChartSettings={userChartSettings}
                        />
                    </GridItem>
                }
            </Grid>

            {children}

        </PageSection>
    );

};

export default NotificationsPage;